import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/textarea/textarea.js';
import { choose } from 'lit/directives/choose.js';
import { consume } from '@lit/context';
import { ltnToastServiceContext } from '../../../../ns-services/contexts.js';

import { LtnToastService } from '../../../../ltn-toast/ltn-toast-service.js';

import { ButtressEntity } from '@buttress/crag';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import { callLambda } from '../../../../helpers/lambda.js';
import '../../../../ns-elements/ns-job-layout.js';
import { formatDate } from '../../../../helpers/format.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/subscription/cancel.json';
import { jobSchema } from '../../job-schema.js';

enum JobStatus {
  RETRACTING_STAGE,
  DOWNGRADE_STAGE,
  UPGRADE_STAGE,
  DISCOUNT_STAGE,
  CANCEL_STAGE,
}

@customElement(NSCancelSubscription.is)
export default class NSCancelSubscription extends NSJobComponent {
  static get is() {
    return 'ns-cancel-subscription';
  }

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  private __subscription?: ButtressEntity;

  private __cancellationReason?: string;

  private __feedback?: string;

  @state()
  private __jobStatus: JobStatus = JobStatus.RETRACTING_STAGE;

  @state()
  private __renewalDate?: string;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .job-panel ul {
        padding-inline-start: var(--wa-space-l);
        font-weight: 300;
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this.__toast) throw new Error('Unable to find toast service');

    if (!this._job) throw new Error('Unable to find job');

    if (!this._directory) throw new Error('Directory service not found');
    if (!this._directory.me || !this._directory.me.companyId)
      throw new Error('Company id not found in the directory service');

    this.__subscription = await this.loadInput('subscription', 'subscriptionId');

    const jobStatusMetadata = this._job.metadata.find((m) => m.key === 'JOB_STATUS');
    if (jobStatusMetadata && jobStatusMetadata.value) {
      this.__jobStatus = Number(jobStatusMetadata.value);
    }

    this.__renewalDate = formatDate(this.__subscription.renewalDate, 'longDate');
  }

  private async __submitFeedback() {
    this.__toast?.queueToast({
      label: "Sorry, we can't take feedback here yet — please email support@nodestream.com and we'll get back to you.",
      timeout: 6000,
    });
  }

  private async __computeJobStatus() {
    if (!this._db) throw new Error('Unable to find db service');
    if (!this._job) throw new Error('Unable to find job');
    if (!this.__subscription)
      throw new Error(`Could not find subscription with ID: ${this._job.inputValues.subscriptionId}`);

    if (this.__jobStatus === JobStatus.RETRACTING_STAGE && this.__subscription.plan === 'MONTHLY') {
      this.__jobStatus = JobStatus.UPGRADE_STAGE;
    }
    if (this.__jobStatus === JobStatus.RETRACTING_STAGE && this.__subscription.plan === 'YEARLY') {
      this.__jobStatus = JobStatus.DOWNGRADE_STAGE;
    }
    if (this.__jobStatus === JobStatus.UPGRADE_STAGE || this.__jobStatus === JobStatus.DOWNGRADE_STAGE) {
      this.__jobStatus = JobStatus.DISCOUNT_STAGE;
    }
    if (this.__jobStatus === JobStatus.DISCOUNT_STAGE) {
      this.__jobStatus = JobStatus.CANCEL_STAGE;
    }

    await this.updateMetadata('JOB_STATUS', String(this.__jobStatus));
  }

  private async __upgradeSubscription() {
    try {
      this.clearError();
      if (!this._auth) throw new Error('Missing auth service');
      if (!this._directory || !this._directory.me) throw new Error('Missing directory service');
      if (!this._job) throw new Error('Unable to find job');
      if (!this.__subscription)
        throw new Error(`Could not find subscription with ID: ${this._job.inputValues.subscriptionId}`);

      this.loading('Starting the plan change');
      await callLambda(
        'subscription/create-management-workflow',
        'POST',
        this._auth.token,
        JSON.stringify({
          personId: this._directory.me.id,
          subscriptionId: this.__subscription.id,
          companyId: this.__subscription.companyId,
          workflow: 'update-subscription-plan',
        }),
      );
      this.loaded();
    } catch (err) {
      this.loaded();
      this.showError(err, 'Unable to start the plan change, please try again.');
    }
  }

  private async __requestSubscriptionDiscount() {
    this.__toast?.queueToast({
      label:
        "Sorry, we can't apply this discount automatically yet — please email support@nodestream.com to arrange it.",
      timeout: 6000,
    });
  }

  private async __cancelSubscription() {
    try {
      this.clearError();
      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__subscription) {
        throw new Error('Unable to find a subscription to be cancelled');
      }

      if (!this.__cancellationReason) {
        throw new Error('Please provide a cancellation reason');
      }

      this.loading('Cancelling Subscription');

      await callLambda(
        'subscription/cancel',
        'POST',
        this._auth.token,
        JSON.stringify({
          subscriptionId: this.__subscription.id,
          cancellationReason: this.__cancellationReason,
        }),
      );

      this.loaded();
      await this.outcome('complete');
    } catch (err) {
      this.loaded();
      this.showError(err, 'Something went wrong, our support is working on fixing it and they will contact you');
    }
  }

  private __textareaValue(e: CustomEvent) {
    const [eventTarget] = e.composedPath();
    return (eventTarget as HTMLTextAreaElement).value;
  }

  render() {
    return html`
      <ns-job-layout>
        ${choose(this.__jobStatus, [
          [
            JobStatus.RETRACTING_STAGE,
            () => html`
              <p slot="intro">We're sorry to see you considering cancelling your subscription.</p>
              <div class="job-panel">
                <h3>We'd love to hear your thoughts!</h3>
                <p>
                  Your satisfaction means everything to us. Before you go, could you please take a moment to let us know
                  how we can improve your experience? If there's anything that could make you reconsider your
                  cancellation, please let us know.
                </p>
                <wa-textarea
                  placeholder="Feedback"
                  @input="${(e: CustomEvent) => (this.__feedback = this.__textareaValue(e))}"
                ></wa-textarea>
                <wa-button variant="brand" type="button" @click=${() => this.__submitFeedback()}>
                  Submit Feedback
                </wa-button>
              </div>
              <div slot="aside" class="primary-info">
                <span class="info-icon" aria-hidden="true">i</span>
                <p>
                  If you are reconsidering cancelling your subscription please submit a feedback to our support and we
                  will get back in touch as soon as possible.
                </p>
                <p>If you are still wanting to cancel, click continue.</p>
              </div>
            `,
          ],
          [
            JobStatus.DOWNGRADE_STAGE,
            () => html`
              <p slot="intro">
                We noticed that you're considering cancelling your yearly subscription with us, and we're really sorry
                to see you go.
              </p>
              <div class="job-panel">
                <h3>How about switching to a monthly plan instead?</h3>
                <p>
                  Before you make your final decision, we wanted to offer you an alternative that might better suit your
                  needs.
                </p>
                <p>
                  While it's slightly more expensive than the yearly option, it gives you the flexibility of no
                  long-term commitment. You can enjoy the same great service without being locked into a full year.
                </p>
              </div>
              <div slot="aside" class="primary-info">
                <span class="info-icon" aria-hidden="true">i</span>
                <p>
                  Please note that this downgrade option is available only during the last month before your
                  subscription renewal date: ${this.__renewalDate}. This gives you the flexibility to switch without
                  committing to another full year.
                </p>
                <p class="note">
                  If you'd like to explore this option click cancel and don't forget to downgrade your subscription.
                </p>
              </div>
            `,
          ],
          [
            JobStatus.UPGRADE_STAGE,
            () => html`
              <p slot="intro">
                We noticed that you're thinking about cancelling your monthly subscription, and we're truly sorry to see
                you go.
              </p>
              <div class="job-panel">
                <h3>Have you considered switching to a yearly subscription?</h3>
                <p>
                  Before you make your final decision, we wanted to offer you a way to keep enjoying our service while
                  saving some money.
                </p>
                <p>
                  By choosing a yearly plan, you can enjoy significant savings compared to paying month-to-month. While
                  it does involve a longer commitment, it's a great way to reduce your overall cost and still get all
                  the benefits you love.
                </p>
                <p>
                  If this sounds like a good fit or if you have any questions, we're here to help. We'd love to have you
                  stay with us!
                </p>
                <wa-button variant="brand" type="button" @click=${() => this.__upgradeSubscription()}>
                  Upgrade
                </wa-button>
              </div>
              <div slot="aside" class="primary-info">
                <span class="info-icon" aria-hidden="true">i</span>
                <h3>Here's how it works</h3>
                <ul class="ticks">
                  <li>
                    <strong>Lower Cost:</strong>
                    Save 10% off your subscription by paying for a full year upfront.
                  </li>
                  <li>
                    <strong>Peace of Mind:</strong>
                    No need to worry about monthly renewals—just set it and forget it.
                  </li>
                  <li>
                    <strong>Same Great Service:</strong>
                    You'll continue to enjoy everything our service offers, just at a better price.
                  </li>
                </ul>
              </div>
            `,
          ],
          [
            JobStatus.DISCOUNT_STAGE,
            () => html`
              <p slot="intro">
                We understand that you're still considering cancelling your subscription, and while we're sorry to hear
                that, we'd like to offer you one last incentive to stay with us.
              </p>
              <div class="job-panel">
                <h3>Here's a special offer just for you</h3>
                <ul>
                  <li>
                    <strong>10% Off Your Next Monthly Subscription:</strong>
                    If you decide to stick with your monthly plan, we'll apply a 10% discount on your next billing
                    cycle.
                  </li>
                  <li>
                    <strong>20% Off a Yearly Subscription:</strong>
                    If you're open to committing for a year, you can save even more with 20% off a yearly subscription.
                    This is our best deal, giving you the maximum savings while continuing to enjoy our service.
                  </li>
                </ul>
                <p>
                  We truly value your presence in our community and hope this offer makes it easier for you to stay with
                  us. If you have any questions or need assistance applying the discount, just let us know—we're here to
                  help!
                </p>
                <wa-button variant="brand" type="button" @click=${() => this.__requestSubscriptionDiscount()}>
                  Upgrade
                </wa-button>
              </div>
            `,
          ],
          [
            JobStatus.CANCEL_STAGE,
            () => html`
              <p slot="intro">
                We're sorry to see you go, but we completely understand and respect your decision to cancel your
                subscription.
              </p>
              <div class="job-panel">
                <p>
                  We want to take this moment to thank you for being a part of our community and for the time you've
                  spent with us. Your feedback and support is invaluable.
                </p>
                <p>
                  If there's anything we can assist you with in the future or if you ever decide to return, we'll be
                  here to welcome you back with open arms. In the meantime, we wish you all the best.
                </p>
                <wa-textarea
                  placeholder="Cancellation Reason"
                  @input="${(e: CustomEvent) => (this.__cancellationReason = this.__textareaValue(e))}"
                ></wa-textarea>
              </div>
              <div slot="aside" class="primary-info">
                <span class="info-icon" aria-hidden="true">i</span>
                <p>
                  Your subscription will be cancelled on: ${this.__renewalDate} and you won't be charged moving forward.
                </p>
              </div>
            `,
          ],
        ])}
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this.__jobStatus === JobStatus.CANCEL_STAGE) {
      return html`
        <wa-button variant="danger" type="button" @click=${() => this.__cancelSubscription()}>
          Cancel Subscription
        </wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" @click=${() => this.__computeJobStatus()}>Continue</wa-button>
      ${this.__jobStatus === JobStatus.DOWNGRADE_STAGE
        ? html`
            <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.outcome('complete')}>
              Cancel
            </wa-button>
          `
        : nothing}
    `;
  }
}
